/**
 * PreviewExportNotice - One-time dismissible notice about preview vs export differences
 *
 * Shows a modal explaining that in-browser preview (vips-wasm) may differ slightly
 * from final export (Sati GPU pipeline). Only shows once per user, dismissed state is
 * persisted to localStorage.
 */

import { useState, useEffect, useCallback } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { STORAGE_KEYS } from '../../../utils/constants';

/**
 * @param {Object} props
 * @param {boolean} props.hasAdjustments - Whether the photo has adjustments applied
 * @returns {JSX.Element|null}
 */
export default function PreviewExportNotice({ hasAdjustments }) {
  const [showNotice, setShowNotice] = useState(false);

  // Check if notice should be shown when adjustments are first applied
  useEffect(() => {
    if (hasAdjustments) {
      const dismissed = localStorage.getItem(STORAGE_KEYS.PREVIEW_EXPORT_NOTICE_DISMISSED);
      if (!dismissed) {
        setShowNotice(true);
      }
    }
  }, [hasAdjustments]);

  const dismissNotice = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.PREVIEW_EXPORT_NOTICE_DISMISSED, 'true');
    setShowNotice(false);
  }, []);

  if (!showNotice) return null;

  return (
    <Modal isOpen={showNotice} onClose={dismissNotice} size="sm">
      <Modal.Header onClose={dismissNotice}>About Adjustments Preview</Modal.Header>
      <Modal.Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ margin: 0, color: 'var(--text)' }}>
            The preview you see is rendered quickly in your browser.
          </p>
          <p style={{ margin: 0, color: 'var(--text)' }}>
            When you mark a photo as done, we render the final export,
            which may produce subtle differences in how adjustments appear.
          </p>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem' }}>
            This ensures the highest quality output while keeping the editing
            experience fast and responsive.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={dismissNotice}>Got it</Button>
      </Modal.Footer>
    </Modal>
  );
}
